/**
 * Pure helper functions for the AdminTicketManagement component.
 * Extracted to reduce file size.
 */
import type { AdminTicket } from "@sd/types/admin";
import type { StreamTicket } from "@sd/lib/ticketStreamUtils";
import { looksLikeOpaqueUserId, requesterLooksHuman } from "@sd/lib/ticketRowMapping";
import { getUserDisplayFromMap } from "@sd/lib/userDisplayMap";
import { sanitizeStatusFilterValues } from "@sd/constants/ticketStatuses";
import { sd } from "@sd/lib/sdPaths";

export const ASSIGNED_SELECT_NONE = "__assign_none__";

/** Accumulate filter dropdown values so later API filters do not shrink options. */
export function mergeStableStringOptions(
  previous: string[],
  incoming: string[]
): string[] {
  const next = new Set(previous);
  let changed = false;
  for (const value of incoming) {
    const v = String(value ?? "").trim();
    if (!v || next.has(v)) continue;
    next.add(v);
    changed = true;
  }
  if (!changed) return previous;
  return [...next].sort((a, b) => a.localeCompare(b));
}

/** Keep the currently selected filter visible even if absent from latest API page. */
export function withSelectedFilterOption(
  options: string[],
  selected: string,
  allValue = "all"
): string[] {
  if (!selected || selected === allValue) return options;
  if (options.includes(selected)) return options;
  return [...options, selected].sort((a, b) => a.localeCompare(b));
}

/** Keep all selected multi-filter values visible even if absent from latest options. */
export function withSelectedMultiFilterOptions(
  options: string[],
  selected: string[]
): string[] {
  const unique = [...new Set(selected.map(v => String(v ?? "").trim()).filter(Boolean))];
  if (unique.length === 0) return options;
  const next = new Set(options);
  let changed = false;
  for (const value of unique) {
    if (next.has(value)) continue;
    next.add(value);
    changed = true;
  }
  if (!changed) return options;
  return [...next].sort((a, b) => a.localeCompare(b));
}

// ── Agent employee list normalizer ────────────────────────────────────────────

/** Parse GET /service_desk/agent_employee_list/ payload into `{ id, name }` rows. */
export function normalizeAgentEmployeeList(
  raw: unknown
): { id: string; name: string }[] {
  const bucket: unknown[] = [];
  if (Array.isArray(raw)) {
    bucket.push(...raw);
  } else if (raw && typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    for (const k of [
      "results",
      "data",
      "employees",
      "agents",
      "users",
      "members",
      "assigned",
      "assigned_users",
      "project_assigned",
      "project_assigned_users",
      "items",
      "records",
      "rows",
      "grid_data",
    ]) {
      const v = o[k];
      if (Array.isArray(v)) { bucket.push(...v); break; }
    }
  }
  const seen = new Set<string>();
  const out: { id: string; name: string }[] = [];
  bucket.forEach((item, idx) => {
    const r = (item ?? {}) as Record<string, unknown>;
    const id = String(
      r.id ??
        r.user_id ??
        r.employee_id ??
        r.emp_id ??
        r.agent_id ??
        r.uuid ??
        r.pk ??
        `agent-${idx}`
    ).trim();
    const combined = [
      r.firstname ?? r.first_name ?? r.firstName,
      r.lastname ?? r.last_name ?? r.lastName,
    ]
      .filter(Boolean)
      .map(String)
      .join(" ")
      .trim();
    const name =
      String(
        r.fullname ??
          r.employee_name ??
          r.full_name ??
          r.fullName ??
          r.name ??
          r.display_name ??
          r.displayName ??
          combined ??
          ""
      ).trim() ||
      String(r.email ?? r.username ?? "").trim() ||
      id;
    if (!name || seen.has(id)) return;
    seen.add(id);
    out.push({ id, name });
  });
  return out;
}

// ── Assignee helpers ──────────────────────────────────────────────────────────

export function assigneeMatchId(
  ticket: AdminTicket,
  agents: { id: string; name: string }[]
): string | null {
  const a = ticket.assignedAgent?.trim();
  if (!a) return null;
  return agents.find(o => o.id === a)?.id ?? agents.find(o => o.name === a)?.id ?? null;
}

/** Readable name for Assigned To (matches `agent_employee_list` id or name). */
export function assigneeDisplayName(
  ticket: AdminTicket,
  agents: { id: string; name: string }[],
  userDisplayMap?: Map<string, string>
): string {
  const raw = ticket.assignedAgent?.trim();
  if (!raw) return "Unassigned";
  const resolved =
    agents.find(o => o.id === raw)?.name ??
    agents.find(o => o.name === raw)?.name ??
    (looksLikeOpaqueUserId(raw)
      ? getUserDisplayFromMap(userDisplayMap, raw)
      : undefined);
  if (resolved) return resolved;
  if (looksLikeOpaqueUserId(raw)) return "Assigned";
  return raw;
}

/** Ensure the ticket's current assignee appears in the dropdown with a readable label. */
export function enrichAssigneeOptionsForTicket(
  agents: { id: string; name: string }[],
  ticket: AdminTicket,
  userDisplayMap?: Map<string, string>
): { id: string; name: string }[] {
  const raw = ticket.assignedAgent?.trim();
  if (!raw) return agents;

  const matchedId = assigneeMatchId(ticket, agents);
  const assigneeId =
    matchedId ?? (looksLikeOpaqueUserId(raw) ? raw : null);
  if (!assigneeId || agents.some(a => a.id === assigneeId)) {
    return agents;
  }

  const name =
    (!looksLikeOpaqueUserId(raw) ? raw : undefined) ??
    getUserDisplayFromMap(userDisplayMap, assigneeId) ??
    "Assigned user";

  return [...agents, { id: assigneeId, name }];
}

export function resolveAssigneeSelectValue(
  ticket: AdminTicket,
  agents: { id: string; name: string }[]
): string {
  const matched = assigneeMatchId(ticket, agents);
  if (matched) return matched;
  const raw = ticket.assignedAgent?.trim();
  if (raw && looksLikeOpaqueUserId(raw)) return raw;
  return ASSIGNED_SELECT_NONE;
}

export function resolvedAssigneeId(
  raw: string | null | undefined,
  agents: { id: string; name: string }[]
): string | null {
  if (raw === undefined || raw === null || String(raw).trim() === "") return null;
  const t = String(raw).trim();
  if (agents.some(a => a.id === t)) return t;
  return agents.find(a => a.name === t)?.id ?? t;
}

export function ticketMatchesAssigneeFilter(
  ticket: AdminTicket,
  filter: string,
  agents: { id: string; name: string }[]
): boolean {
  if (filter === "all") return true;
  const resolvedId =
    assigneeMatchId(ticket, agents) ?? resolvedAssigneeId(ticket.assignedAgent, agents);
  const rawAssignee = ticket.assignedAgent?.trim() ?? "";
  if (filter === ASSIGNED_SELECT_NONE) return !resolvedId && !rawAssignee;
  if (resolvedId === filter) return true;
  const agent = agents.find(a => a.id === filter);
  return Boolean(agent && rawAssignee === agent.name);
}

// ── Grid merge helper ─────────────────────────────────────────────────────────

/** Agent panel: keep assignee name visible until list API catches up after save. */
export function mergeStreamTicketAssignOverrides(
  rows: StreamTicket[],
  assignOverrides: Map<string, string | null>,
  agents: { id: string; name: string }[]
): StreamTicket[] {
  if (assignOverrides.size === 0) return rows;
  return rows.map(row => {
    const want = assignOverrides.get(row.id);
    if (want === undefined) return row;
    const serverId = resolvedAssigneeId(row.assignedAgent, agents);
    const matches =
      want === null
        ? serverId === null
        : serverId === want ||
          row.assignedAgent === want ||
          (typeof row.assignedAgent === "string" &&
            agents.find(a => a.id === want)?.name === row.assignedAgent);
    if (matches) {
      assignOverrides.delete(row.id);
      return row;
    }
    const label =
      want === null ? null : (agents.find(a => a.id === want)?.name ?? want);
    return { ...row, assignedAgent: label };
  });
}

/**
 * Keeps server-saved assignments visible while `grid_data` lags or drops `assigned_to`.
 * `assignOverrides`: ticketId → assignee UUID or `null` (unassigned) from last successful save.
 */
export function mergeTicketListFromGrid(
  prev: AdminTicket[],
  server: AdminTicket[],
  assignOverrides: Map<string, string | null>,
  agents: { id: string; name: string }[],
  lastKnownUiId: Map<string, string>,
  lastKnownRequesterDisplay: Map<string, string>
): AdminTicket[] {
  if (prev.length === 0 && assignOverrides.size === 0) {
    return server.map(s => {
      const fromRef = lastKnownUiId.get(s.id);
      let next = s.uiId ? s : fromRef ? { ...s, uiId: fromRef } : s;
      if (next.uiId) lastKnownUiId.set(next.id, next.uiId);
      const reqRef = lastKnownRequesterDisplay.get(s.id);
      const rq = (next.requester ?? "").trim();
      if (reqRef && (!rq || looksLikeOpaqueUserId(rq))) next = { ...next, requester: reqRef };
      if (requesterLooksHuman(next.requester)) lastKnownRequesterDisplay.set(next.id, next.requester.trim());
      return next;
    });
  }
  const prevById = new Map(prev.map(t => [t.id, t]));
  return server.map(s => {
    const oldRow = prevById.get(s.id);
    let next: AdminTicket;

    const want = assignOverrides.get(s.id);
    if (want !== undefined) {
      const serverId = resolvedAssigneeId(s.assignedAgent, agents);
      const matches =
        want === null
          ? serverId === null
          : serverId === want ||
            s.assignedAgent === want ||
            (typeof s.assignedAgent === "string" &&
              agents.find(a => a.id === want)?.name === s.assignedAgent);
      if (matches) {
        assignOverrides.delete(s.id);
        next = s;
      } else {
        const label = want === null ? null : (agents.find(a => a.id === want)?.name ?? want);
        next = { ...s, assignedAgent: label };
      }
    } else if (!oldRow) {
      next = s;
    } else {
      const trim = (a: unknown): string | null => {
        if (typeof a !== "string") return null;
        const t = a.trim();
        return t === "" ? null : t;
      };
      next = !trim(s.assignedAgent) && trim(oldRow.assignedAgent)
        ? { ...s, assignedAgent: oldRow.assignedAgent }
        : s;
    }

    if (oldRow?.uiId && !next.uiId) next = { ...next, uiId: oldRow.uiId };
    const fromRef = lastKnownUiId.get(s.id);
    if (!next.uiId && fromRef) next = { ...next, uiId: fromRef };
    if (next.uiId) lastKnownUiId.set(next.id, next.uiId);

    const srvRq = typeof next.requester === "string" ? next.requester.trim() : String(next.requester ?? "");
    if (oldRow && requesterLooksHuman(oldRow.requester) && (!srvRq || looksLikeOpaqueUserId(srvRq))) {
      next = { ...next, requester: oldRow.requester.trim() };
    }
    const rqRef = lastKnownRequesterDisplay.get(s.id);
    const curRq = (next.requester ?? "").trim();
    if (rqRef && (!curRq || looksLikeOpaqueUserId(curRq))) next = { ...next, requester: rqRef };
    if (requesterLooksHuman(next.requester)) lastKnownRequesterDisplay.set(next.id, next.requester.trim());
    return next;
  });
}

/** Admin dashboard Recent Tickets → ticket list (excludes closed via `not_close`). */
export const ADMIN_RECENT_TICKETS_FROM_QUERY = "from";
export const ADMIN_RECENT_TICKETS_FROM_VALUE = "recent-tickets";

export function buildAdminRecentTicketsListPath(): string {
  const params = new URLSearchParams();
  params.set(ADMIN_RECENT_TICKETS_FROM_QUERY, ADMIN_RECENT_TICKETS_FROM_VALUE);
  return `${sd("admin/tickets")}?${params.toString()}`;
}

export function isAdminRecentTicketsListContext(
  searchParams: URLSearchParams
): boolean {
  return (
    searchParams.get(ADMIN_RECENT_TICKETS_FROM_QUERY) ===
    ADMIN_RECENT_TICKETS_FROM_VALUE
  );
}

/** Admin ticket list deep-link from dashboard / agents page. */
export function buildAdminAgentInProgressTicketsPath(
  agentId: string,
  options?: { projectFilters?: string[] }
): string {
  const id = agentId.trim();
  if (!id) return sd("admin/tickets");

  const params = new URLSearchParams();
  params.set("assigned_to", id);
  params.set("status", "in_progress");
  const projects = (options?.projectFilters ?? []).map(p => p.trim()).filter(Boolean);
  if (projects.length > 0) params.set("project", projects.join(","));

  return `${sd("admin/tickets")}?${params.toString()}`;
}

export function parseAdminTicketListDeepLinkSearchParams(
  searchParams: URLSearchParams
): {
  assignedTo?: string;
  unassigned: boolean;
  statusFilters: string[];
  projectFilters: string[];
} | null {
  const assignedTo = searchParams.get("assigned_to")?.trim();
  const unassigned = searchParams.get("unassigned") === "true";
  const statusRaw = searchParams.get("status")?.trim();
  const projectRaw = searchParams.get("project")?.trim();

  const statusFilters = statusRaw
    ? sanitizeStatusFilterValues(
        statusRaw
          .split(",")
          .map(s => s.trim())
          .filter(Boolean)
      )
    : [];
  const projectFilters = projectRaw
    ? projectRaw
        .split(",")
        .map(s => s.trim())
        .filter(Boolean)
    : [];

  if (!assignedTo && !unassigned && statusFilters.length === 0 && projectFilters.length === 0) {
    return null;
  }

  return {
    assignedTo: assignedTo || undefined,
    unassigned,
    statusFilters,
    projectFilters,
  };
}
